import { Alert } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { PageSkeleton } from '../../../Common/Skeletons';
import UnsupportedFileView from '../UnsupportedFileView';
import './DocumentViewer.css';

/**
 * DocumentViewer — client-side preview for Office documents (docx / xlsx / pptx).
 *
 * Used by BOTH viewer shells (MediaViewerPanel + FullPageMediaViewer) for the
 * 'document' media type, exactly like PDFViewer / FileViewer. Everything renders
 * in the browser from the fetched ArrayBuffer — the signed URL and file content
 * never leave the user's machine (we deliberately avoid the Office/Google iframe
 * viewers, which require publicly fetchable URLs and ship the document to a
 * third party).
 *
 * Renderers (all lazy — dynamic import() keeps them out of the main bundle):
 *   .docx        → docx-preview `renderAsync` (Word-like pages)
 *   .xlsx / .xls → SheetJS values + ExcelJS styles → xlsxStyledRenderer, plus sheet tabs.
 *                  Text colors are decided against the viewer surface (light/dark), so a
 *                  theme flip rebuilds the tables from the cached workbooks (no refetch).
 *                  (SheetJS is pinned to the official cdn.sheetjs.com tarball in
 *                  package.json — the npm registry copy is stale at 0.18.5 with
 *                  known CVEs; see CVE-2023-30533 / CVE-2024-22363)
 *   .pptx        → pptx-preview `init(...).preview(buffer)` (slide list)
 *   .doc / .ppt  → legacy binary formats, no client renderer → UnsupportedFileView
 */

/**
 * Same-origin URLs get credentials; cross-origin signed URLs (GCS) must NOT —
 * adding credentials to signed URLs causes CORS failures. (Same rule as FileViewer.)
 */
const needsCredentials = (url) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    return true;
  }
};

const EXT = (filename) => {
  if (!filename) return '';
  const i = filename.lastIndexOf('.');
  return i === -1 ? '' : filename.slice(i).toLowerCase();
};

const MIME_DOCX = 'wordprocessingml';
const MIME_XLSX = 'spreadsheetml';
const MIME_PPTX = 'presentationml';

/** Which concrete renderer handles this document. */
export const detectDocKind = (filename, mimeType) => {
  const ext = EXT(filename);
  const mime = (mimeType || '').toLowerCase();

  if (ext === '.docx' || mime.includes(MIME_DOCX)) return 'docx';
  if (ext === '.xlsx' || ext === '.xls' || mime.includes(MIME_XLSX) || mime.includes('ms-excel')) return 'xlsx';
  if (ext === '.pptx' || mime.includes(MIME_PPTX)) return 'pptx';
  // .doc / .ppt (pre-2007 binary formats): no viable client-side renderer.
  return 'unsupported';
};

/**
 * xlsx: styled HTML per sheet for the given viewer surface. `src` is the parsed-workbook
 * bundle cached in xlsxRef (SheetJS workbook, ExcelJS workbook or null, SheetJS utils,
 * and the renderer). The renderer escapes all cell text — safe for dangerouslySetInnerHTML.
 */
const buildSheets = ({ workbook, exWb, utils, build }, isDarkMode) => workbook.SheetNames.map((name) => ({
  name,
  html: build(workbook.Sheets[name], exWb ? exWb.getWorksheet(name) : null, utils, { isDarkMode }),
}));

const DocumentViewer = ({ url, filename, fileSize, isDarkMode, mimeType }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // xlsx only: [{ name, html }] per sheet + the active tab index
  const [sheets, setSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  const containerRef = useRef(null);
  // pptx-preview returns a previewer instance with a destroy() — keep it for cleanup.
  const pptxPreviewerRef = useRef(null);
  // xlsx only: parsed workbooks kept so a theme flip can re-render without refetching.
  const xlsxRef = useRef(null);
  // Latest theme for the async render below (the fetch effect must not re-run on theme change).
  const isDarkRef = useRef(isDarkMode);
  isDarkRef.current = isDarkMode;

  const kind = detectDocKind(filename, mimeType);

  useEffect(() => {
    if (kind === 'unsupported') return undefined;

    let cancelled = false;

    const render = async () => {
      try {
        setLoading(true);
        setError(null);
        setSheets(null);
        setActiveSheet(0);
        xlsxRef.current = null;

        const fetchOptions = needsCredentials(url) ? { credentials: 'include' } : {};
        const response = await fetch(url, fetchOptions);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
        const buffer = await response.arrayBuffer();
        if (cancelled) return;

        if (kind === 'docx') {
          const { renderAsync } = await import('docx-preview');
          if (cancelled || !containerRef.current) return;
          containerRef.current.innerHTML = '';
          await renderAsync(buffer, containerRef.current, null, {
            className: 'docx',
            inWrapper: true,
            breakPages: true,
            ignoreWidth: false,
            ignoreHeight: false,
          });
        } else if (kind === 'xlsx') {
          // Values from SheetJS + styles from ExcelJS — see xlsxStyledRenderer for why both.
          const [XLSX, ExcelJSmod, { buildStyledSheetHtml }] = await Promise.all([
            import('xlsx'),
            import('exceljs'),
            import('./xlsxStyledRenderer'),
          ]);
          if (cancelled) return;
          const workbook = XLSX.read(buffer, { type: 'array', cellStyles: true });
          // Style pass is best-effort: ExcelJS can't load legacy .xls — fall back to plain values.
          let exWb = null;
          try {
            const ExcelJS = ExcelJSmod.default || ExcelJSmod;
            exWb = new ExcelJS.Workbook();
            await exWb.xlsx.load(buffer);
          } catch { exWb = null; }
          if (cancelled) return;
          xlsxRef.current = { workbook, exWb, utils: XLSX.utils, build: buildStyledSheetHtml };
          setSheets(buildSheets(xlsxRef.current, isDarkRef.current));
        } else if (kind === 'pptx') {
          const { init } = await import('pptx-preview');
          if (cancelled || !containerRef.current) return;
          containerRef.current.innerHTML = '';
          // Slide width tracks the container, clamped so: tiny panels stay legible (>=320),
          // huge screens don't blow up (<=1280), and a 16:9 slide always FITS the viewer
          // height (fullscreen: no oversized slide + dead scroll below).
          // IMPORTANT: no `height` option — in list mode pptx-preview would fix its wrapper at
          // that height with its own overflow-y scroll (nested scrollbars + a black void below
          // the slides). Without it the wrapper grows to fit and our render-target scrolls.
          const el = containerRef.current;
          const fitByHeight = Math.round(((el.clientHeight - 32) * 16) / 9);
          const width = Math.max(Math.min(el.clientWidth - 32, 1280, fitByHeight), 320);
          const previewer = init(el, { width, mode: 'list' });
          pptxPreviewerRef.current = previewer;
          await previewer.preview(buffer);
        }

        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error('[DocumentViewer] render failed', err);
        if (!cancelled) {
          setError('Failed to preview this document. It may be corrupted, or use unsupported features.');
          setLoading(false);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      if (pptxPreviewerRef.current) {
        try { pptxPreviewerRef.current.destroy(); } catch { /* already torn down */ }
        pptxPreviewerRef.current = null;
      }
    };
  }, [url, kind]);

  // Theme flip (xlsx): text colors are chosen against the surface, so rebuild the tables from
  // the cached workbooks — no refetch. No-op until the first parse has landed.
  useEffect(() => {
    if (kind !== 'xlsx' || !xlsxRef.current) return;
    setSheets(buildSheets(xlsxRef.current, isDarkMode));
  }, [isDarkMode, kind]);

  // Legacy .doc / .ppt — same download-first experience as any other unviewable type.
  if (kind === 'unsupported') {
    return (
      <UnsupportedFileView
        filename={filename}
        fileSize={fileSize}
        mimeType={mimeType}
        url={url}
        isDarkMode={isDarkMode}
      />
    );
  }

  if (error) {
    return (
      <div className={`document-viewer-error ${isDarkMode ? 'dark' : 'light'}`}>
        <Alert
          type="error"
          message="Document Preview Failed"
          description={
            <div>
              <p>{error}</p>
              <p>Please try downloading the file to view it locally.</p>
            </div>
          }
          showIcon
        />
      </div>
    );
  }

  return (
    <div className={`document-viewer document-viewer-${kind} ${isDarkMode ? 'dark' : 'light'}`}>
      {loading && (
        <div className="document-viewer-loading">
          <PageSkeleton />
        </div>
      )}

      {/* xlsx — sheet tabs + rendered table (React-owned) */}
      {kind === 'xlsx' && sheets && sheets.length > 0 && (
        <>
          {sheets.length > 1 && (
            <div className="document-viewer-sheet-tabs" role="tablist">
              {sheets.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  role="tab"
                  aria-selected={i === activeSheet}
                  className={`document-viewer-sheet-tab ${i === activeSheet ? 'active' : ''}`}
                  onClick={() => setActiveSheet(i)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div
            className="document-viewer-sheet-content"
            // xlsxStyledRenderer escapes all cell text — no raw user HTML lands here.
            dangerouslySetInnerHTML={{ __html: sheets[activeSheet].html }}
          />
        </>
      )}

      {/* docx / pptx — the libraries render directly into this node */}
      {(kind === 'docx' || kind === 'pptx') && (
        <div
          ref={containerRef}
          className="document-viewer-render-target"
          style={{ opacity: loading ? 0 : 1 }}
        />
      )}
    </div>
  );
};

export default DocumentViewer;
